import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type DashboardStats } from '@/lib/admin';
import { StatCard, SectionCard, LoadingState, ErrorState } from '@/pages/admin/components/UI';

const quickLinks = [
  { path: '/admin/users', label: '用户管理', icon: 'ri-user-settings-line', desc: '查看与管理用户' },
  { path: '/admin/orders', label: '订单管理', icon: 'ri-file-list-3-line', desc: '查看全部订单' },
  { path: '/admin/notices', label: '公告管理', icon: 'ri-megaphone-line', desc: '发布与编辑公告' },
  { path: '/admin/deposits', label: '入金管理', icon: 'ri-currency-line', desc: '确认充值到账' },
  { path: '/admin/withdrawals', label: '出金管理', icon: 'ri-bank-line', desc: '审核提现申请' },
  { path: '/admin/pickups', label: '提货管理', icon: 'ri-truck-line', desc: '审核提货申请' },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.dashboard();
      setStats(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="space-y-5">
      {/* 欢迎条 */}
      <div className="bg-gradient-to-r from-brown-900 to-brown-800 rounded-xl p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-white text-lg md:text-xl font-bold">欢迎回来，管理员</h2>
          <p className="text-brown-300 text-sm mt-1">这里是CJJY运营管理后台，实时掌握平台动态。</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/deposits"
            className="inline-flex items-center gap-2 bg-gold-500 text-brown-950 text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap hover:bg-gold-600 transition-colors"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-currency-line text-base"></i>
            </span>
            处理待入金
            {(stats?.pending_deposits ?? 0) > 0 && (
              <span className="bg-red-500 text-white text-[10px] min-w-4 h-4 px-1 rounded-full flex items-center justify-center">
                {stats?.pending_deposits}
              </span>
            )}
          </Link>
          <Link
            to="/admin/withdrawals"
            className="inline-flex items-center gap-2 bg-gold-400 text-brown-950 text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap hover:bg-gold-300 transition-colors"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-bank-line text-base"></i>
            </span>
            处理待出金
            {(stats?.pending_withdrawals ?? 0) > 0 && (
              <span className="bg-red-500 text-white text-[10px] min-w-4 h-4 px-1 rounded-full flex items-center justify-center">
                {stats?.pending_withdrawals}
              </span>
            )}
          </Link>
        </div>
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState onRetry={fetchData} />
      ) : (
        stats && (
          <>
            {/* 实时持仓明细 — 置顶 */}
            <SectionCard title="实时持仓明细">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 md:p-5">
                <div className="flex flex-col items-center justify-center p-4 rounded-lg border border-gold-200 bg-gold-50/50">
                  <span className="text-gold-600 text-xs font-medium mb-1">黄金持仓</span>
                  <span className="text-brown-900 text-xl font-bold">
                    {stats.holding_gold.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}g
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center p-4 rounded-lg border border-brown-200 bg-brown-50/50">
                  <span className="text-brown-600 text-xs font-medium mb-1">白银持仓</span>
                  <span className="text-brown-900 text-xl font-bold">
                    {stats.holding_silver.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}g
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center p-4 rounded-lg border border-gold-200 bg-gold-50/50">
                  <span className="text-gold-600 text-xs font-medium mb-1">公斤条持仓</span>
                  <span className="text-brown-900 text-xl font-bold">
                    {stats.holding_kgbar.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}g
                  </span>
                </div>
              </div>
            </SectionCard>

            {/* 核心数据 */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 md:gap-4">
              <StatCard icon="ri-user-3-line" label="注册用户" value={stats.total_users} tone="gold" />
              <StatCard icon="ri-file-list-3-line" label="全部订单" value={stats.total_orders} tone="brown" />
              <StatCard icon="ri-lock-line" label="持仓中订单" value={stats.open_orders} tone="gold" />
              <StatCard icon="ri-currency-line" label="待确认入金" value={stats.pending_deposits} tone="green" />
              <StatCard icon="ri-bank-line" label="待审核出金" value={stats.pending_withdrawals} tone="red" />
              <StatCard icon="ri-truck-line" label="待审核提货" value={stats.pending_pickups} tone="gold" />
              <StatCard
                icon="ri-wallet-3-line"
                label="用户总余额"
                value={`¥${stats.total_balance.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`}
                tone="gold"
              />
              <StatCard
                icon="ri-scales-3-line"
                label="持仓总克重"
                value={`${stats.total_holding_weight.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}g`}
                tone="brown"
              />
            </div>


            <SectionCard title="功能入口">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 md:p-5">
                {quickLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="flex items-center gap-3 p-4 rounded-lg border border-brown-100 hover:border-gold-300 hover:bg-gold-50/40 transition-colors"
                  >
                    <div className="w-11 h-11 rounded-lg bg-gold-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-6 h-6 flex items-center justify-center text-gold-700">
                        <i className={`${link.icon} text-xl`}></i>
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-brown-900 text-sm font-bold">{link.label}</p>
                      <p className="text-brown-400 text-xs truncate">{link.desc}</p>
                    </div>
                    <span className="w-5 h-5 flex items-center justify-center text-brown-300">
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </span>
                  </Link>
                ))}
              </div>
            </SectionCard>
          </>
        )
      )}
    </div>
  );
}